/**
 * Copyright 2021-2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { checkUrl } from '@umati/dashboard-common';
import {
  SectionRowElement,
  SectionRowElementBadge,
  SectionRowElementChart,
  SectionRowElementList,
  SectionRowElementTable,
  SectionRowElementText,
  SectionRowElementTypeEnum,
} from '@umati/dashboard-types';
import SectionData from 'components/machine/sectionData';
import React, { Component } from 'react';
import TemplateHelper, { DEFAULT_VALUE_UNDEFINED } from 'utilities/TemplateHelper';
import SectionElementContentChart from './SectionElementContentChart';
import SectionElementContentList from './SectionElementContentList';
import SectionElementContentTable from './SectionElementContentTable';

export interface SectionElementContentProps {
  element: SectionRowElement;
  cols: string;
  data: any;
}

export interface SectionElementContentState {}

export default class SectionElementContent extends Component<SectionElementContentProps, SectionElementContentState> {
  constructor(props: SectionElementContentProps) {
    super(props);
    this.state = {};
    this.getLabel = this.getLabel.bind(this);
  }

  getLabel(props: SectionElementContentProps) {
    if (props.element.isPlaceholder) {
      return props.element.labelVariable
        ? TemplateHelper.getVariableValue(props.data, props.element.labelVariable)
        : '';
    } else {
      return props.element.label ? TemplateHelper.getTextValue(props.element.label) : '';
    }
  }

  render() {
    if (!TemplateHelper.checkRenderingCondition(this.props.data, this.props.element.condition)) {
      return <></>;
    }
    const variableValue = TemplateHelper.getVariableValue(
      this.props.data,
      this.props.element.valueVariable,
      DEFAULT_VALUE_UNDEFINED,
      this.props.element.customUnit,
    );
    return (
      <>
        {this.props.element.type === SectionRowElementTypeEnum.TEXT && (
          <>
            {(this.props.element as SectionRowElementText).showAsLink && checkUrl(variableValue) ? (
              <SectionData cols={this.props.cols} label={this.getLabel(this.props)}>
                <a href={variableValue}>{variableValue}</a>
              </SectionData>
            ) : (
              <SectionData cols={this.props.cols} label={this.getLabel(this.props)} value={variableValue} />
            )}
          </>
        )}
        {this.props.element.type === SectionRowElementTypeEnum.CHART && (
          <SectionElementContentChart
            element={this.props.element as SectionRowElementChart}
            cols={this.props.cols}
            data={this.props.data}
            label={this.getLabel(this.props)}
          />
        )}
        {this.props.element.type === SectionRowElementTypeEnum.BADGE && (
          <SectionData cols={this.props.cols} label={this.getLabel(this.props)}>
            <span
              className="umati-badge"
              style={{
                backgroundColor: TemplateHelper.getBadgeBackgroundColor(
                  this.props.element as SectionRowElementBadge,
                  variableValue,
                ),
                color: TemplateHelper.getBadgeTextColor(this.props.element as SectionRowElementBadge, variableValue),
              }}
            >
              {TemplateHelper.getBadgeDisplayValue(this.props.element as SectionRowElementBadge, variableValue)}
            </span>
          </SectionData>
        )}
        {this.props.element.type === SectionRowElementTypeEnum.TABLE && (
          <SectionElementContentTable
            element={this.props.element as SectionRowElementTable}
            data={this.props.data}
            label={this.getLabel(this.props)}
          />
        )}
        {this.props.element.type === SectionRowElementTypeEnum.LIST && (
          <SectionElementContentList
            element={this.props.element as SectionRowElementList}
            cols={this.props.cols}
            data={this.props.data}
            label={this.getLabel(this.props)}
          />
        )}
      </>
    );
  }
}
